<template>
  <div class="widget w-gainers-losers-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      csvExport
      fieldsConfigManager="advanced"
      hideLinkModeReceive
      optimizeColumns
      printView
      :wid="wid"
      @csv-export="onCsvExport"
      @optimize-columns="autoSizeColumns"
    >
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar>
      <c-widget-control-bar-group inline>
        <v-form-group :label="i18n('controls.labels.exchange')" label-cols="auto">
          <v-form-select
            :options="optionsExchange"
            :value="controlExchange"
            @input="onExchangeChange"
            :aria-label="i18n('controls.labels.exchange')"
          />
        </v-form-group>
        <v-form-group :label="i18n('controls.labels.interval')" label-cols="auto">
          <v-form-select
            :options="optionsInterval"
            :value="controlInterval"
            @input="onIntervalChange"
            :aria-label="i18n('controls.labels.interval')"
          />
        </v-form-group>
        <v-form-group :label="i18n('controls.labels.results')" label-cols="auto">
          <v-form-select
            :options="optionsResults"
            :value="controlResults"
            @input="onResultsChange"
            :aria-label="i18n('controls.labels.results')"
          />
        </v-form-group>
        <v-form-group :label="i18n('controls.labels.display')" label-cols="auto">
          <v-form-select
            :options="optionsDisplay"
            :value="controlDisplay"
            @input="onDisplayChange"
            :aria-label="i18n('controls.labels.display')"
          />
        </v-form-group>
        <v-form-group class="order">
          <v-form-select
            :options="optionsOrder"
            :value="controlOrder"
            @input="onOrderChange"
            :aria-label="i18n('controls.labels.display')"
          />
        </v-form-group>
        <v-form-group :label="i18n('template', 'globalLabels')" label-cols="auto">
          <v-form-select
            :options="fieldsConfigNameOptions"
            v-model="controlFieldsConfigName"
            ref="templateSelect"
            :aria-label="i18n('template', 'globalLabels')"
          />
        </v-form-group>
      </c-widget-control-bar-group>
      <c-widget-control-bar-group inline>
        <!-- <c-widget-input-switch
          label=""
          :options="securitiesTypeOptions"
          :value="controlSecuritiesType"
          @input="onSecuritiesTypeChange"
        /> -->
      </c-widget-control-bar-group>
    </c-widget-control-bar>

    <div class="widget-body">
      <c-loader v-if="isLoading" />
      <c-streaming-error :show="hasStreamingError" />
      <section v-for="section in sections" :key="section" :class="section">
        <h5>{{ i18n('controls.labels.' + section) }}</h5>
        <ag-grid-vue
          v-if="!tableState[section].isError"
          v-show="!isLoading"
          class="c-data-grid ag-theme-balham-dark"
          animateRows
          :asyncTransactionWaitMillis="gridUpdateInterval"
          :columnDefs="columnDefs"
          :defaultColDef="getDefaultColDef(section)"
          ensureDomOrder
          :gridOptions="tableState[section].gridOptions"
          immutableData
          rowSelection="single"
          :rowHeight="agGridRowHeight"
          suppressFieldDotNotation
          suppressContextMenu
          suppressDragLeaveHidesColumns
          :suppressColumnVirtualisation="isAccessibilityEnabled"
          @column-moved="onColMoved"
          @column-resized="onColResized($event, section)"
          @drag-stopped="onGridDragEnd"
          @grid-ready="onTableReady(section)"
          @row-selected="updateLink"
          @sort-changed="onTableSort(section)"
          @cell-focused="tableState[section].onCellFocused"
          @new-columns-loaded="ev => setColumnState(ev.columnApi)"
          @async-transactions-flushed="onAsyncTransactionsFlushed"
        />
        <v-alert show v-else-if="tableState[section].isError === 'nodata'" variant="light" align="center">{{
          i18n('messages.nodata')
        }}</v-alert>
        <v-alert show v-else variant="red" align="center">{{ i18n('messages.error') }}</v-alert>
      </section>
    </div>
  </div>
</template>

<script>
import agGridCommons from '@mixins/agGridCommons.js';
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetInputSwitch from '@custom/widgetContainer/CWidgetInputSwitch';
import { API } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';
import streamableFidsFilter from '@/utils/streamableFids';
import { GenericTickerRenderer, RowButtonRenderer } from '@custom/dataGrid/renderers';
import moment from 'moment';

export default {
  name: 'w-gainers-losers-widget',
  mixins: [agGridCommons],
  components: {
    AgGridVue,
    CLoader,
    CStreamingError,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    CWidgetHeader,
    CWidgetInputSwitch,
    GenericTickerRenderer,
    RowButtonRenderer
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      hasStreamingError: false,
      optionsExchange: [
        // { value: 'GER', text: 'Xetra Level 1' }, // for testing purposes
        { value: 'TOR', text: 'TSX' },
        { value: 'CVE', text: 'TSX Venture' },
        { value: 'NYQ', text: 'NYSE' },
        { value: 'ASQ', text: 'AMEX' },
        { value: 'NSQ', text: 'NASDAQ' }
      ],
      optionsResults: [5, 10, 20],
      pollingInterval: 60, //s
      reconnectOnErrorTime: 30, //s
      resizeTimeout: 0,
      tableState: {
        gainers: {
          gridOptions: {
            context: this,
            getRowNodeId: d => d['q.RIC'],
            columnTypes: { actionable: {} },
            tabToNextHeader: this.tabToNextHeader,
            tabToNextCell: this.tabToNextCellGainers
          },
          isError: false,
          isLoading: false,
          pollingTimeout: 0,
          subscriptionId: null,
          onCellFocused: params => this.onCellFocused(params, this.$el.querySelector('section.gainers'))
        },
        losers: {
          gridOptions: {
            context: this,
            getRowNodeId: d => d['q.RIC'],
            columnTypes: { actionable: {} },
            tabToNextHeader: this.tabToNextHeaderLosers,
            tabToNextCell: this.tabToNextCellLosers
          },
          isError: false,
          isLoading: false,
          pollingTimeout: 0,
          subscriptionId: null,
          onCellFocused: params => this.onCellFocused(params, this.$el.querySelector('section.losers'))
        }
      },
      tableFieldsPrepend: ['action']
    };
  },
  computed: {
    controlDisplay() {
      return this.widgetStore.display;
    },
    controlExchange() {
      return this.widgetStore.exchange;
    },
    controlInterval() {
      return this.widgetStore.interval;
    },
    controlOrder() {
      return this.widgetStore.order;
    },
    controlResults() {
      return this.widgetStore.results;
    },
    isLoading() {
      return Object.values(this.tableState).some(tableConfig => {
        return tableConfig.isLoading && !tableConfig.isError;
      });
    },
    optionsDisplay() {
      return [
        { value: 'both', text: this.i18n('controls.labels.both') },
        { value: 'gainers', text: this.i18n('controls.labels.gainers') },
        { value: 'losers', text: this.i18n('controls.labels.losers') }
      ];
    },
    optionsInterval() {
      return [
        { value: '5m', text: 5 + this.i18n('controls.labels.min') },
        { value: '15m', text: 15 + this.i18n('controls.labels.min') },
        { value: 'h', text: 60 + this.i18n('controls.labels.min') },
        { value: 'd', text: this.i18n('controls.labels.allDay') }
      ];
    },
    optionsOrder() {
      return [
        { value: 1, text: this.i18n('controls.labels.gainers') + '/' + this.i18n('controls.labels.losers') },
        { value: -1, text: this.i18n('controls.labels.losers') + '/' + this.i18n('controls.labels.gainers') }
      ];
    },
    sections() {
      const sectionNames = Object.keys(this.tableState);
      if (this.controlDisplay === 'both') {
        return this.controlOrder === 1 ? sectionNames : sectionNames.reverse();
      } else {
        return [sectionNames.find(sectionName => sectionName === this.controlDisplay)];
      }
    },
    extendedColDefs() {
      return {
        action: {
          cellRenderer: 'RowButtonRenderer',
          width: 40
        },
        'q._TCR': {
          cellRenderer: 'GenericTickerRenderer',
          cellRendererParams: params => {
            return { ticker: params.data['q._TCR'], tradeHaltValue: params.data['q.TRD_STATUS'] };
          }
        }
      };
    }
  },
  methods: {
    getDefaultColDef(section) {
      return {
        ...this.defaultColumnDef,
        sortable: false,
        suppressHeaderKeyboardEvent: params => this.suppressHeaderKeyboardEvent(params, section),
        suppressKeyboardEvent: params => this.suppressKeyboardEvent(params, section)
      };
    },
    suppressHeaderKeyboardEvent(params, section) {
      const key = params.event.code || params.event.key;
      const isTab = key === 'Tab';
      const isFirstSection = this.sections[0] === section;

      if (!isTab) return false;
      else if (!params.event.shiftKey) {
        //header to cell tab
        params.event.preventDefault();
        this.focusTableCell(params.event, section);
        return true;
      } else if (params.event.shiftKey) {
        //header to previous element in tree
        params.event.preventDefault();
        if (isFirstSection) {
          this.$refs.templateSelect.focus();
        } else {
          const otherSection = this.sections.find(v => v !== section);
          this.focusTableCell(params.event, otherSection, true);
        }
        return true;
      }
    },
    suppressKeyboardEvent(params, section) {
      const key = params.event.code || params.event.key;
      const tab = key === 'Tab';
      if (tab) {
        const allCols = params.columnApi.getAllDisplayedColumns();
        const firstCol = allCols[0],
          lastCol = allCols[allCols.length - 1];
        const isFirstSection = this.sections[0] === section;
        const rowCount = params.api.getModel().getRowCount();
        const otherSection = this.sections.find(v => v !== section);
        if (params.node.rowIndex === 0 && params.column.colId === firstCol.colId && params.event.shiftKey) {
          params.event.preventDefault();
          if (isFirstSection) {
            this.$refs.templateSelect.focus();
          } else {
            this.focusTableCell(params.event, otherSection, true);
          }
          return true;
        } else if (
          isFirstSection &&
          otherSection &&
          params.node.rowIndex === rowCount - 1 &&
          params.column.colId === lastCol.colId &&
          !params.event.shiftKey
        ) {
          params.event.preventDefault();
          this.focusTableCell(params.event, otherSection);
          return true;
        }
      }
      return false;
    },
    autoSizeColumns() {
      Object.values(this.tableState).forEach(
        tableConfig => tableConfig.gridOptions.columnApi && tableConfig.gridOptions.columnApi.autoSizeAllColumns(true)
      );
    },
    onDisplayChange(ev) {
      this.$store.commit(`widget/${this.wid}/DISPLAY_CHANGED`, ev);
      Object.keys(this.tableState).forEach(tableName => {
        if ([tableName, 'both'].includes(this.controlDisplay)) this.fetchForSection(tableName);
        else this.streamingUnsubscribe(tableName);
      });
    },
    onExchangeChange(ev) {
      this.$store.commit(`widget/${this.wid}/EXCHANGE_CHANGED`, ev);
      this.fetch();
    },
    onIntervalChange(ev) {
      this.$store.commit(`widget/${this.wid}/INTERVAL_CHANGED`, ev);
      this.fetch();
    },
    onOrderChange(ev) {
      this.$store.commit(`widget/${this.wid}/ORDER_CHANGED`, ev);
    },
    onResultsChange(ev) {
      this.$store.commit(`widget/${this.wid}/RESULTS_CHANGED`, ev);
      this.fetch();
    },
    getStockScreenerData(ns) {
      return API.Rest.get(
        '/stockscreener/' +
          ns +
          '?exchanges=' +
          this.controlExchange +
          '&fids=' +
          ['stoscr.RIC', ...this.requestFids.filter(fid => fid.startsWith('stoscr.'))] +
          '&interval=' +
          this.controlInterval +
          '&count=' +
          this.controlResults +
          `&sortBy=${ns === 'losers' ? '0.0-' : ''}PERFORMANCE`
      ).then(response => {
        if (!response.data.data.length) return Promise.reject('nodata');
        return [ns, response.data.data];
      });
    },
    getSnapshot([ns, stockscreenerData]) {
      const rics = stockscreenerData.map(row => row['stoscr.RIC']);
      return API.Rest.get(`/quote/info?rics=${rics}&fids=${this.requestFids}`).then(response => {
        this._.difference(this.requestFids, Object.keys(this.formats)).forEach(fid => {
          if (response.data.metadata[fid] && response.data.metadata[fid].type)
            this.formats[fid] = response.data.metadata[fid].type;
        });
        this.tableState[ns].isError = false;
        const rowsData = [];
        response.data.data.forEach(data => {
          const row = this.tableState[ns].gridOptions.api.getRowNode(data['q.RIC']);
          Object.assign(data, stockscreenerData.find(stoscrRow => stoscrRow['stoscr.RIC'] === data['q.RIC']));
          rowsData.push(this.hydrateData(data, row ? row.data : {}));
        });
        this.tableState[ns].gridOptions.api.setRowData(rowsData);
        this.tableState[ns].isLoading = false;
        this.tableState[ns].pollingTimeout = setTimeout(() => {
          if (document.visibilityState === 'visible') {
            this.fetchForSection(ns, true);
          }
        }, this.pollingInterval * 1000);
        this.tableState[ns].rics = rics;
        return ns;
      });
    },
    streamingSubscribe(ns) {
      return this.streamingConnect().then(() => {
        this.tableState[ns].subscriptionId = API.Streaming.Quote.subscribe(
          {
            rics: this.tableState[ns].rics,
            fids: streamableFidsFilter(this.requestFids)
          },
          // Subscribe Callback
          (_response, error) => {
            if (error) {
              this.hasStreamingError = true;
              console.warn('Streaming subscribe error', error);
            } else {
              this.hasStreamingError = false;
            }
          },
          // Snapshot Callback
          (response, error) => this.handleSnapshot(ns, response, error),
          // Update Callback
          (response, error) => this.handleUpdate(ns, response, error)
        );
      });
    },
    streamingUnsubscribe(ns) {
      this.streamingConnect().then(() => {
        if (!this.tableState[ns].subscriptionId) return;
        const subId = this.tableState[ns].subscriptionId;
        API.Streaming.Quote.unsubscribe(subId, (/*e , err*/) => {
          if (this.tableState[ns].subscriptionId === subId) this.tableState[ns].subscriptionId = null;
        });
      });
    },
    handleSnapshot(ns, response, error) {
      if (error) {
        console.warn('Streaming snapshot error', error);
        this.hasStreamingError = true;
        return;
      }
      this.handleUpdate(ns, response);
    },
    handleUpdate(ns, response, error) {
      if (error) {
        console.warn('Streaming update error', error);
        this.hasStreamingError = true;
        return;
      }
      this.hasStreamingError = false;
      response = response.payload[0];
      const rowNode = this.tableState[ns].gridOptions.api.getRowNode(response.ric);
      const data = this.hydrateData(response.data, rowNode.data);
      this.tableState[ns].gridOptions.api.applyTransactionAsync({ update: [data] });
    },
    fetch() {
      this.sections.forEach(tableName => {
        this.fetchForSection(tableName);
      });
    },
    fetchForSection(ns, silent) {
      if (!silent) this.tableState[ns].isLoading = true;
      clearTimeout(this.tableState[ns].pollingTimeout);
      this.tableState[ns].isError = false;
      this.streamingUnsubscribe(ns);
      if (this.tableState[ns].gridOptions.api && !silent) this.tableState[ns].gridOptions.api.setRowData([]);
      this.getStockScreenerData(ns)
        .then(this.getSnapshot)
        .then(this.streamingSubscribe)
        .catch(e => {
          if (e === 'nodata') this.tableState[ns].isError = 'nodata';
          else {
            console.error(e);
            this.tableState[ns].isError = true;
            this.tableState[ns].pollingTimeout = setTimeout(() => {
              if (document.visibilityState === 'visible' && this.sections.includes(ns)) {
                this.fetchForSection(ns);
              }
            }, this.reconnectOnErrorTime * 1000);
          }
        });
    },
    // TODO consider merging with the common onCsvExport in agGridCommons
    onCsvExport() {
      this.sections.forEach(section => {
        this.tableState[section].gridOptions.api.exportDataAsCsv({
          fileName: section + '.export.csv',
          columnKeys: this.tableState[section].gridOptions.columnApi.getAllDisplayedColumns().reduce((acc, col) => {
            if (!this.excludedCsvExportFids.includes(col.colDef.field)) acc.push(col.colId);
            return acc;
          }, []),
          skipGroups: true,
          processCellCallback(params) {
            if (params.value === undefined || params.value === null || params.value.toString() === 'NaN') return '';
            if (params.value instanceof Date) return params.value.toLocaleDateString('en-US');

            let momentDate = (momentDate = moment(params.value, 'DD MMM YYYY', true));
            if (momentDate.isValid()) {
              return momentDate.format('MM/DD/YYYY');
            }

            return params.value.toString();
          }
        });
      });
    },
    onTableReady(section) {
      this.setRowsHeight(section);
      if (this.widgetStore[section + 'Sort']) {
        this.setSortState(this.widgetStore[section + 'Sort'], this.tableState[section].gridOptions.columnApi);
      }
      if (this.widgetStore.columnState[section]) {
        this.tableState[section].gridOptions.columnApi.applyColumnState({
          state: this.widgetStore.columnState[section]
        });
      } else {
        this.tableState[section].gridOptions.columnApi.autoSizeAllColumns(true);
      }
      this.setHeaderRowObservers();
      this.tableState['gainers'].gridOptions.api.setGridAriaProperty('label', this.i18n('controls.labels.gainers'));
      this.tableState['losers'].gridOptions.api.setGridAriaProperty('label', this.i18n('controls.labels.losers'));
    },
    onTableSort(section) {
      this.$store.commit(
        `widget/${this.wid}/${section.toUpperCase()}_SORT_CHANGED`,
        this.getSortState(this.tableState[section].gridOptions.columnApi)
      );
    },
    setRowsHeight(filterSection) {
      this.sections.forEach(section => {
        if (filterSection && section !== filterSection) return;
        this.tableState[section].gridOptions.api.resetRowHeights();
        this.tableState[section].gridOptions.api.setHeaderHeight(this.agGridRowHeight);
      });
    },
    focusTableCell(event, section = this.sections[0], lastCell = false) {
      /*Focus the table cell for specified section, and when 'lastCell' set behaves like 'End' keyboard event*/
      const gridOptions = this.tableState[section].gridOptions;
      const index = lastCell ? gridOptions.api.getModel().getRowCount() - 1 : 0;
      gridOptions.api.ensureIndexVisible(index); // scrolls to the first row

      const cols = gridOptions.columnApi.getAllDisplayedColumns();
      const colToFocus = lastCell ? cols[cols.length - 1] : cols[0];
      gridOptions.api.ensureColumnVisible(colToFocus);

      // sets focus into the first grid cell
      gridOptions.api.setFocusedCell(index, colToFocus);
    },
    tabToNextHeaderLosers(params) {
      const nextHeader = this.tabToNextHeader(params);
      if (!nextHeader) {
        const gainersGridOptions = this.tableState['gainers'].gridOptions;
        const columns = gainersGridOptions.columnApi.getAllDisplayedColumns();
        gainersGridOptions.api.setFocusedCell(
          gainersGridOptions.api.getDisplayedRowCount() - 1,
          columns[columns.length - 1].colDef.colId
        );
      }
      return nextHeader;
    },
    tabToNextCellGainers(params) {
      return this.tabToNextCell(params, this.$el.querySelector('section.gainers'));
    },
    tabToNextCellLosers(params) {
      return this.tabToNextCell(params, this.$el.querySelector('section.losers'));
    },
    onTemplateKeyDown(event) {
      if (event.key === 'Tab' && !event.shiftKey) this.focusTableCell(event);
    }
  },
  // lifecycle
  mounted() {
    //this is to skip the focus on ag-grid headers when tabbing
    this.$refs.templateSelect.$el.addEventListener('keydown', this.onTemplateKeyDown);
    this.fetch();
  },
  beforeDestroy() {
    this.sections.forEach(tableName => {
      this.streamingUnsubscribe(tableName);
      clearTimeout(this.tableState[tableName].pollingTimeout);
    });
    this.$refs.templateSelect.$el.removeEventListener('keydown', this.onTemplateKeyDown);
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/constituents-widget';
@import '~@/styles/widgets/gainers-losers-widget';
</style>
